/**
 * Utilitário genérico para busca paginada de registros
 *
 * O Supabase tem limite de 1000 registros por query.
 * Este utilitário pagina automaticamente para buscar TODOS os registros.
 *
 * PADRÃO: Use este utilitário em TODOS os dropdowns/selects que podem ter mais de 1000 registros.
 *
 * Entidades que DEVEM usar este padrão:
 * - pessoa (colaboradores, clientes, contatos)
 * - produto (use fetchAllProducts de productSearch.js)
 * - obra
 * - proposta
 * - oportunidade
 * - lancamento (financeiro)
 * - qualquer tabela que possa crescer indefinidamente
 *
 * @example
 * // Buscar todas as pessoas
 * const pessoas = await fetchAllRecords(supabase, {
 *   table: 'pessoa',
 *   select: 'id, apelido, nome',
 *   orderBy: 'apelido'
 * });
 *
 * // Buscar com filtro
 * const pessoasAtivas = await fetchAllRecords(supabase, {
 *   table: 'pessoa',
 *   select: 'id, apelido',
 *   orderBy: 'apelido',
 *   filters: [{ column: 'ativo', value: true }]
 * });
 *
 * // Buscar com join
 * const obrasComCliente = await fetchAllRecords(supabase, {
 *   table: 'obra',
 *   select: 'id, obra, cliente:pessoa_id(apelido)',
 *   orderBy: 'obra'
 * });
 */

import type { SupabaseClient } from '@supabase/supabase-js';

import { ReconnectingError, isReconnectingError } from '@/services/errors';

type Row = Record<string, unknown>;

type Filter = {
  column: string;
  operator?: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'like' | 'ilike' | 'in' | 'is';
  value: unknown;
};

export type FetchAllOptions = {
  table?: string;
  select?: string;
  orderBy?: string;
  ascending?: boolean;
  filters?: Filter[];
  pageSize?: number;
};

export type SelectOption = {
  value: unknown;
  label: string;
  searchText: string;
};

export type MapOptions = {
  valueField?: string;
  labelFn?: ((row: Row) => string) | string;
  searchFields?: string[];
};

type FetchAllForSelectOptions = FetchAllOptions & MapOptions;

type EntityFetchOptions = {
  filters?: Filter[];
  returnRaw?: boolean;
};

// Dynamic table names blow up strict Supabase typings; cast to untyped at the boundary.
const asUntyped = (c: SupabaseClient): SupabaseClient => c as unknown as SupabaseClient;

/**
 * Normaliza texto removendo acentos e convertendo para minúsculas
 */
export const normalizeText = (text: unknown): string => {
  if (!text || typeof text !== 'string') return '';
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Carrega TODOS os registros de uma tabela com paginação automática
 *
 * @param {Object} supabase - Cliente Supabase
 * @param {Object} options - Opções de configuração
 * @param {string} options.table - Nome da tabela
 * @param {string} options.select - Campos a selecionar (padrão: '*')
 * @param {string} options.orderBy - Campo para ordenação (padrão: 'id')
 * @param {boolean} options.ascending - Ordem ascendente (padrão: true)
 * @param {Array} options.filters - Array de filtros [{ column, operator, value }]
 * @param {number} options.pageSize - Tamanho da página (padrão: 1000)
 * @returns {Promise<Array>} Array com todos os registros
 * @throws {ReconnectingError} quando a sessão está reconectando (JOSH-89)
 */
export const fetchAllRecords = async (
  supabase: SupabaseClient,
  options: FetchAllOptions = {}
): Promise<Row[]> => {
  const {
    table,
    select = '*',
    orderBy = 'id',
    ascending = true,
    filters = [],
    pageSize = 200
  } = options;

  if (!table) {
    console.error('[fetchAllRecords] Parâmetro "table" é obrigatório');
    return [];
  }

  // Filtros efetivos (inclui filtros automáticos por tabela)
  const effectiveFilters: Filter[] = [...filters];

  // PRODUTO: Filtrar apenas ativos por padrão (a menos que já tenha filtro is_ativo)
  if (table === 'produto' && !filters.some((f: Filter) => f.column === 'is_ativo')) {
    effectiveFilters.push({ column: 'is_ativo', operator: 'eq', value: true });
  }

  let page = 0;
  let allRecords: Row[] = [];

  while (true) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    let query = asUntyped(supabase)
      .from(table)
      .select(select)
      .order(orderBy, { ascending })
      .range(from, to);

    // Aplicar filtros
    for (const filter of effectiveFilters) {
      const { column, operator = 'eq', value } = filter;
      if (operator === 'eq') {
        query = query.eq(column, value);
      } else if (operator === 'neq') {
        query = query.neq(column, value);
      } else if (operator === 'gt') {
        query = query.gt(column, value);
      } else if (operator === 'gte') {
        query = query.gte(column, value);
      } else if (operator === 'lt') {
        query = query.lt(column, value);
      } else if (operator === 'lte') {
        query = query.lte(column, value);
      } else if (operator === 'like') {
        query = query.like(column, String(value));
      } else if (operator === 'ilike') {
        query = query.ilike(column, String(value));
      } else if (operator === 'in') {
        query = query.in(column, value as readonly unknown[]);
      } else if (operator === 'is') {
        query = query.is(column, value);
      }
    }

    const { data, error } = await query;

    if (error) {
      // JOSH-89: a reconnecting session must never look like an empty or
      // partial list; the caller keeps what it shows and reloads later.
      if (isReconnectingError(error)) throw new ReconnectingError(error);
      console.error(`[fetchAllRecords] Erro ao carregar ${table} (página ${page}):`, error);
      break;
    }

    const rows = (data as unknown as Row[]) ?? [];
    if (rows.length === 0) break;

    allRecords = allRecords.concat(rows);

    // Se retornou menos que o pageSize, é a última página
    if (rows.length < pageSize) break;

    page++;
  }

  return allRecords;
};

/**
 * Cria o searchText para busca client-side no SearchableSelect
 * Concatena múltiplos campos e normaliza (sem acentos, lowercase)
 *
 * @param {Object} record - Objeto do registro
 * @param {Array<string>} fields - Campos a concatenar para busca
 * @returns {string} Texto normalizado para busca
 *
 * @example
 * const searchText = createSearchText(pessoa, ['apelido', 'nome', 'email']);
 */
export const createSearchText = (record: Row, fields: readonly string[]): string => {
  const valores = fields.map((field: string) => {
    // Suporta campos aninhados como 'cidade.nome'
    const parts = field.split('.');
    let value: unknown = record;
    for (const part of parts) {
      value = value && typeof value === 'object' ? (value as Row)[part] : undefined;
      if (value === undefined || value === null) break;
    }
    return value ?? '';
  });
  return normalizeText(valores.filter(Boolean).join(' '));
};

/**
 * Mapeia registros para o formato do SearchableSelect
 *
 * @param {Array} records - Array de registros
 * @param {Object} options - Opções de mapeamento
 * @param {string} options.valueField - Campo para o value (padrão: 'id')
 * @param {Function|string} options.labelFn - Função ou campo para criar o label
 * @param {Array<string>} options.searchFields - Campos para criar o searchText
 * @returns {Array} Array no formato { value, label, searchText }
 *
 * @example
 * // Label simples (campo único)
 * const options = mapToSelectOptions(pessoas, {
 *   labelFn: 'apelido',
 *   searchFields: ['apelido', 'nome', 'email']
 * });
 *
 * // Label customizado (função)
 * const options = mapToSelectOptions(pessoas, {
 *   labelFn: (p) => `${p.apelido} - ${p.nome}`,
 *   searchFields: ['apelido', 'nome', 'email']
 * });
 */
export const mapToSelectOptions = (
  records: readonly Row[],
  options: MapOptions = {}
): SelectOption[] => {
  const {
    valueField = 'id',
    labelFn,
    searchFields = []
  } = options;

  return records.map((record: Row) => {
    // Value
    const value = record[valueField];

    // Label - pode ser string (nome do campo) ou função
    let label: string;
    if (typeof labelFn === 'function') {
      label = labelFn(record);
    } else if (typeof labelFn === 'string') {
      label = String(record[labelFn] ?? '');
    } else {
      label = String(record[valueField] ?? '');
    }

    // SearchText - concatena campos e normaliza
    const searchText = searchFields.length > 0
      ? createSearchText(record, searchFields)
      : normalizeText(label);

    return { value, label, searchText };
  });
};

/**
 * Versão simplificada para casos comuns
 * Carrega todos os registros e mapeia para SearchableSelect
 *
 * @param {Object} supabase - Cliente Supabase
 * @param {Object} options - Opções combinadas de fetch e mapeamento
 * @returns {Promise<Array>} Array no formato { value, label, searchText }
 *
 * @example
 * // Buscar todas as pessoas para um dropdown
 * const pessoasOptions = await fetchAllForSelect(supabase, {
 *   table: 'pessoa',
 *   select: 'id, apelido, nome, email',
 *   orderBy: 'apelido',
 *   labelFn: 'apelido',
 *   searchFields: ['apelido', 'nome', 'email']
 * });
 *
 * // Buscar obras com cliente
 * const obrasOptions = await fetchAllForSelect(supabase, {
 *   table: 'obra',
 *   select: 'id, obra, cliente:pessoa_id(apelido)',
 *   orderBy: 'obra',
 *   labelFn: (o) => `${o.obra} - ${o.cliente?.apelido || 'Sem cliente'}`,
 *   searchFields: ['obra', 'cliente.apelido']
 * });
 */
export const fetchAllForSelect = async (
  supabase: SupabaseClient,
  options: FetchAllForSelectOptions = {}
): Promise<SelectOption[]> => {
  const {
    table,
    select,
    orderBy,
    ascending,
    filters,
    pageSize,
    valueField,
    labelFn,
    searchFields
  } = options;

  // Buscar todos os registros
  const records = await fetchAllRecords(supabase, {
    table,
    select,
    orderBy,
    ascending,
    filters,
    pageSize
  });

  // Mapear para formato do SearchableSelect
  return mapToSelectOptions(records, {
    valueField,
    labelFn,
    searchFields
  });
};

// ============================================
// FUNÇÕES ESPECÍFICAS POR ENTIDADE
// Use estas funções prontas para entidades comuns
// ============================================

/**
 * Carrega todas as pessoas para dropdown
 * Inclui: id, apelido, nome, email
 * Busca em: apelido, nome, email
 */
export const fetchAllPessoas = async (
  supabase: SupabaseClient,
  options: EntityFetchOptions = {}
): Promise<Row[] | SelectOption[]> => {
  const { filters = [], returnRaw = false } = options;

  const records = await fetchAllRecords(supabase, {
    table: 'pessoa',
    select: 'id, apelido, nome, email',
    orderBy: 'apelido',
    filters
  });

  if (returnRaw) return records;

  return mapToSelectOptions(records, {
    labelFn: 'apelido',
    searchFields: ['apelido', 'nome', 'email']
  });
};

/**
 * Carrega todas as obras para dropdown
 * Inclui: id, obra, obra_status_id
 * Busca em: obra
 */
export const fetchAllObras = async (
  supabase: SupabaseClient,
  options: EntityFetchOptions = {}
): Promise<Row[] | SelectOption[]> => {
  const { filters = [], returnRaw = false } = options;

  const records = await fetchAllRecords(supabase, {
    table: 'obra',
    select: 'id, obra, obra_status_id',
    orderBy: 'obra',
    filters
  });

  if (returnRaw) return records;

  return mapToSelectOptions(records, {
    labelFn: 'obra',
    searchFields: ['obra']
  });
};

/**
 * Carrega todas as oportunidades para dropdown
 * Inclui: id, nome, cliente (pessoa)
 * Busca em: nome, cliente.apelido
 */
export const fetchAllOportunidades = async (
  supabase: SupabaseClient,
  options: EntityFetchOptions = {}
): Promise<Row[] | SelectOption[]> => {
  const { filters = [], returnRaw = false } = options;

  const records = await fetchAllRecords(supabase, {
    table: 'oportunidade',
    select: 'id, nome, cliente:pessoa_id(apelido)',
    orderBy: 'nome',
    filters
  });

  if (returnRaw) return records;

  return mapToSelectOptions(records, {
    labelFn: (o: Row) => {
      const nome = String(o.nome ?? '');
      const cliente = o.cliente as { apelido?: string | null } | null | undefined;
      return cliente?.apelido ? `${nome} - ${cliente.apelido}` : nome;
    },
    searchFields: ['nome', 'cliente.apelido']
  });
};

/**
 * Carrega todas as propostas para dropdown
 * Inclui: id, cod, oportunidade
 * Busca em: cod, oportunidade.nome
 */
export const fetchAllPropostas = async (
  supabase: SupabaseClient,
  options: EntityFetchOptions = {}
): Promise<Row[] | SelectOption[]> => {
  const { filters = [], returnRaw = false } = options;

  const records = await fetchAllRecords(supabase, {
    table: 'proposta',
    select: 'id, cod, oportunidade:oportunidade_id(nome)',
    orderBy: 'cod',
    ascending: false,
    filters
  });

  if (returnRaw) return records;

  return mapToSelectOptions(records, {
    labelFn: (p: Row) => {
      const cod = String(p.cod ?? '');
      const oportunidade = p.oportunidade as { nome?: string | null } | null | undefined;
      return oportunidade?.nome ? `${cod} - ${oportunidade.nome}` : cod;
    },
    searchFields: ['cod', 'oportunidade.nome']
  });
};
